import { S25Assert } from "../util/s25.assert";
import { SocContactUser, SocSkippedUser } from "./standout.classroom.service";

export type SocChannelType = "public" | "private" | "direct";

export function normalizeSocChannelType(type?: string): SocChannelType {
    if (type === "public" || type === "private" || type === "direct") {
        return type;
    }
    return "private";
}

export function buildSocChatEmbedPath(channelType: SocChannelType, externalId: string, socChannelId?: number): string {
    switch (channelType) {
        case "public":
            return `/channel/${externalId}`;
        case "direct":
            if (socChannelId != null) {
                return `/dm/${socChannelId}`;
            }
            console.warn("Chat: direct channel missing socChannelId, falling back to private path");
            return `/private/channel/${externalId}`;
        case "private":
        default:
            return `/private/channel/${externalId}`;
    }
}

export type EventChatType = "all" | "scheduler" | "requestor" | "instructor";
export type WorkflowChatType = "all" | "reviewers" | "submitter";

/**
 * 1:1 workflow chat between two contacts about one submission. The pair is ordered so both people
 * reach the same channel whoever starts it.
 */
export function buildWorkflowPersonChatExternalId(
    submissionIdUuid: string,
    firstContId: number,
    secondContId: number,
): string {
    const [lowerContId, higherContId] = [firstContId, secondContId].sort((first, second) => first - second);
    return `${submissionIdUuid}:person:${lowerContId}:${higherContId}`;
}

export function buildWorkflowPersonChatChannelTitle(enrichedBaseTitle: string, personName: string): string {
    const pipeIdx = enrichedBaseTitle.indexOf(" | ");
    const name = pipeIdx === -1 ? enrichedBaseTitle : enrichedBaseTitle.substring(0, pipeIdx);
    const rest = pipeIdx === -1 ? "" : enrichedBaseTitle.substring(pipeIdx);
    return `${name} — Chat with ${personName}${rest}`;
}

export function buildEventChatExternalId(
    eventId: number | string,
    chatType: EventChatType,
    initiatingContId?: number,
): string {
    const baseId = String(eventId);
    if (chatType === "all") {
        return baseId;
    }
    const roleScoped = `${baseId}:${chatType}`;
    if (initiatingContId != null) {
        return `${roleScoped}:${initiatingContId}`;
    }
    return roleScoped;
}

export function buildWorkflowChatExternalId(
    submissionIdUuid: string,
    chatType: WorkflowChatType,
    initiatingContId?: number,
): string {
    if (chatType === "all") {
        return submissionIdUuid;
    }
    const roleScoped = `${submissionIdUuid}:${chatType}`;
    if (chatType === "submitter" && initiatingContId != null) {
        return `${roleScoped}:${initiatingContId}`;
    }
    return roleScoped;
}

export function buildEventChatChannelTitle(eventName: string, chatType: EventChatType, eventLocator?: string): string {
    const locatorSuffix = eventLocator ? ` (${eventLocator})` : "";
    const baseName = `${eventName}${locatorSuffix}`;
    switch (chatType) {
        case "all":
            return baseName;
        case "scheduler":
            return `${baseName} — Chat with Scheduler`;
        case "requestor":
            return `${baseName} — Chat with Requestor`;
        case "instructor":
            return `${baseName} — Chat with Instructor`;
        default:
            return S25Assert.never(chatType, "Unknown event chat type");
    }
}

export function buildWorkflowChatChannelTitle(enrichedBaseTitle: string, chatType: WorkflowChatType): string {
    if (chatType === "all") {
        return enrichedBaseTitle;
    }
    const roleLabel = chatType === "submitter" ? "Chat with Submitter" : "Chat with Reviewers";
    const pipeIdx = enrichedBaseTitle.indexOf(" | ");
    const name = pipeIdx === -1 ? enrichedBaseTitle : enrichedBaseTitle.substring(0, pipeIdx);
    const rest = pipeIdx === -1 ? "" : enrichedBaseTitle.substring(pipeIdx);
    return `${name} — ${roleLabel}${rest}`;
}

const NO_25LIVE_LOGIN_REASON = "No 25Live login";

export interface WorkflowChatReviewer {
    username?: string;
    contId?: number;
    contact?: {
        firstName?: string;
        familyName?: string;
        email?: string;
    };
}

export interface WorkflowChatSubmitter {
    r25_username?: string;
    contact?: {
        first_name?: string;
        last_name?: string;
        primary_email?: string;
    };
}

export interface WorkflowChatUserPartition {
    users: SocContactUser[];
    skippedUsers: SocSkippedUser[];
}

function reviewerDisplayLabel(reviewer: WorkflowChatReviewer): string {
    const name = [reviewer.contact?.firstName, reviewer.contact?.familyName].filter(Boolean).join(" ");
    return name || String(reviewer.contId ?? "Unknown");
}

function mapReviewerToSocUser(reviewer: WorkflowChatReviewer): SocContactUser {
    return {
        userType: "25live",
        contId: reviewer.contId!,
        username: reviewer.username!,
        firstname: reviewer.contact?.firstName ?? "",
        lastname: reviewer.contact?.familyName ?? "",
        email: reviewer.contact?.email ?? "",
    };
}

function mapSubmitterToSocUser(submitter: WorkflowChatSubmitter, contId: number): SocContactUser {
    return {
        userType: "25live",
        contId,
        username: submitter.r25_username!,
        firstname: submitter.contact?.first_name ?? "",
        lastname: submitter.contact?.last_name ?? "",
        email: submitter.contact?.primary_email ?? "",
    };
}

function partitionReviewers(reviewers: WorkflowChatReviewer[]): WorkflowChatUserPartition {
    const users: SocContactUser[] = [];
    const skippedUsers: SocSkippedUser[] = [];
    for (const reviewer of reviewers) {
        if (reviewer.username && reviewer.contId != null) {
            users.push(mapReviewerToSocUser(reviewer));
        } else {
            skippedUsers.push({
                username: reviewerDisplayLabel(reviewer),
                contId: reviewer.contId,
                reason: NO_25LIVE_LOGIN_REASON,
            });
        }
    }
    return { users, skippedUsers };
}

export function partitionWorkflowChatUsers(
    chatType: WorkflowChatType,
    reviewers: WorkflowChatReviewer[],
    submitter?: WorkflowChatSubmitter | null,
    submitterContId?: number,
): WorkflowChatUserPartition {
    const reviewerPartition = partitionReviewers(reviewers);

    switch (chatType) {
        case "reviewers":
            return reviewerPartition;
        case "submitter": {
            if (!submitterContId) {
                return { users: [], skippedUsers: [] };
            }
            if (submitter?.r25_username) {
                return {
                    users: [mapSubmitterToSocUser(submitter, submitterContId)],
                    skippedUsers: [],
                };
            }
            return {
                users: [],
                skippedUsers: [{ contId: submitterContId, reason: NO_25LIVE_LOGIN_REASON }],
            };
        }
        case "all": {
            const skippedUsers = [...reviewerPartition.skippedUsers];
            const users = [...reviewerPartition.users];
            if (submitterContId != null) {
                if (submitter?.r25_username) {
                    users.push(mapSubmitterToSocUser(submitter, submitterContId));
                } else {
                    skippedUsers.push({ contId: submitterContId, reason: NO_25LIVE_LOGIN_REASON });
                }
            }
            return { users, skippedUsers };
        }
        default:
            return S25Assert.never(chatType, "Unknown workflow chat type");
    }
}
